import React, { type Dispatch, type SetStateAction } from 'react';
import type { ShoppingCartData } from './shoppingCartTypes';
import type { CartSelectionTotals, VumPromoSlice } from './checkoutFormTotals';
import type { User as UserType } from '../../types';
import type { MyCartWizardStep } from './myCartWizardTypes';

type TFn = (key: string) => string;

interface MyCartSidebarCheckoutFooterProps {
  t: TFn;
  shoppingCartData?: ShoppingCartData | null;
  calculatedTotals: CartSelectionTotals;
  showCheckoutOptions: boolean;
  setShowCheckoutOptions: Dispatch<SetStateAction<boolean>>;
  currentUser?: UserType | null;
  selectedItems: Set<string>;
  handleCheckout: (paymentMode?: 'guest' | 'member') => void;
  vumPromo?: VumPromoSlice | null;
  layout?: 'inScroll' | 'pinned';
  cartStep: MyCartWizardStep;
  onGoToStep: (step: MyCartWizardStep) => void;
}

const MyCartSidebarCheckoutFooter: React.FC<MyCartSidebarCheckoutFooterProps> = ({
  t,
  shoppingCartData,
  calculatedTotals,
  showCheckoutOptions,
  setShowCheckoutOptions,
  currentUser,
  selectedItems,
  handleCheckout,
  layout = 'inScroll',
  cartStep,
  onGoToStep,
}) => {
  const hasDates = !!(shoppingCartData?.startDate && shoppingCartData?.endDate);
  const noDatesBlocked = selectedItems.size > 0 && !hasDates;
  const canReview = selectedItems.size > 0 && !calculatedTotals.vumAnyCapacityExceeded;
  const canCheckout = canReview && !noDatesBlocked;

  return (
    <div
      className={`border-t border-black/10 bg-[#e8ddd7]/80 p-3 sm:p-4 ${
        layout === 'pinned' ? 'mt-0' : 'mt-4'
      }`}
    >
      {cartStep === 1 ? (
        <div className="space-y-2">
          {calculatedTotals.vumAnyCapacityExceeded ? (
            <p className="text-xs font-medium text-red-700">
              {t('shoppingCart.capacityBlockedHint') ||
                'Group size exceeds VUM limits — adjust guests or contact us.'}
            </p>
          ) : null}
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-[#3d2817]">
                {selectedItems.size}{' '}
                {selectedItems.size === 1 ? t('common.item') : t('common.items')}
              </p>
              <p className="text-xs text-[#5d4437]">
                {t('common.total')}:{' '}
                <span className="font-semibold text-[#2f9e44]">
                  ${calculatedTotals.total.toFixed(2)}
                </span>
              </p>
            </div>
            <button
              type="button"
              className="shrink-0 rounded-lg bg-nature-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-nature-700 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={!canReview}
              onClick={() => onGoToStep(2)}
            >
              {t('common.next') || 'Next'}
            </button>
          </div>
        </div>
      ) : null}

      {cartStep === 2 ? (
        <div className="flex gap-2">
          <button
            type="button"
            className="flex-1 rounded-lg border border-[#3d2817]/20 px-4 py-2.5 text-sm font-semibold text-[#3d2817] transition-colors hover:bg-white/70"
            onClick={() => onGoToStep(1)}
          >
            {t('common.goBack') || 'Back'}
          </button>
          <button
            type="button"
            className="flex-1 rounded-lg bg-nature-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-nature-700 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={!canCheckout}
            onClick={() => onGoToStep(3)}
          >
            {t('common.next') || 'Next'}
          </button>
        </div>
      ) : null}

      {cartStep === 3 ? (
        <div className="space-y-3">
          <div className="flex items-center justify-between border-b border-gray-300/80 pb-2">
            <span className="text-sm font-medium text-gray-700">{t('common.total')}</span>
            <span className="text-lg font-bold text-nature-600">
              ${calculatedTotals.total.toFixed(2)}
            </span>
          </div>

          {showCheckoutOptions && !currentUser ? (
            <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
              <h3 className="mb-3 text-sm font-semibold text-gray-900">
                {t('common.choosePaymentMethod')}
              </h3>
              <div className="space-y-2">
                <button
                  type="button"
                  className="w-full rounded-lg bg-gray-100 px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200"
                  onClick={() => handleCheckout('guest')}
                >
                  {t('common.checkoutAsGuest')}
                </button>
                <button
                  type="button"
                  className="w-full rounded-lg bg-nature-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-nature-700"
                  onClick={() => handleCheckout('member')}
                >
                  {t('common.checkoutAsMember')}
                </button>
              </div>
              <button
                type="button"
                className="mt-3 w-full text-sm text-gray-500 hover:text-gray-700"
                onClick={() => setShowCheckoutOptions(false)}
              >
                {t('common.cancel')}
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="w-full rounded-lg bg-nature-600 px-4 py-3 font-semibold text-white transition-colors hover:bg-nature-700 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={() => handleCheckout()}
              disabled={
                selectedItems.size === 0 ||
                calculatedTotals.vumAnyCapacityExceeded ||
                noDatesBlocked
              }
            >
              {t('common.checkout')}
            </button>
          )}

          <button
            type="button"
            className="w-full rounded-lg border border-[#3d2817]/20 px-4 py-2.5 text-sm font-semibold text-[#3d2817] transition-colors hover:bg-white/70"
            onClick={() => {
              setShowCheckoutOptions(false);
              onGoToStep(2);
            }}
          >
            {t('common.goBack') || 'Back'}
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default MyCartSidebarCheckoutFooter;
